import React from 'react';
import { Video, ExternalLink, Clock, Eye, Tag } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoCard = ({ video, showMetadata = false, compact = false }) => {
  const getVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const videoUrl = video.youtube_url || video.url;
  const videoId = getVideoId(videoUrl);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  
  // Use source from video object or extract from description
  const getSource = () => {
    if (video.source) return video.source;
    if (!video.description) return 'YouTube';
    const sourceMatch = video.description.match(/Source:\s*([^.]+)/i);
    return sourceMatch ? sourceMatch[1].trim() : 'YouTube';
  };
  
  const source = getSource();
  
  // Get duration label
  const getDurationLabel = () => {
    if (video.duration_label) return video.duration_label;
    if (video.duration) {
      const mins = Math.floor(video.duration / 60);
      const secs = video.duration % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return null;
  };
  
  const durationLabel = getDurationLabel();
  
  // Clean description (remove source attribution as it's shown separately)
  const cleanDescription = (description) => {
    if (!description) return '';
    return description.replace(/\s*Source:\s*[^.]+\.?/i, '').trim();
  };

  // Track video view when clicked
  const handleVideoClick = async () => {
    try {
      await axios.post(`${API}/videos/${video.id}/view`);
    } catch (error) {
      // Silent fail - don't block user
    }
  };

  // Category badge color
  const getCategoryColor = () => {
    switch(video.category) {
      case 'Procedure': return 'category-procedure';
      case 'Condition': return 'category-condition';
      case 'Anatomy': return 'category-anatomy';
      case 'General Education': return 'category-education';
      default: return 'category-default';
    }
  };

  if (compact) {
    return (
      <a 
        href={videoUrl} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="video-card-compact"
        onClick={handleVideoClick}
        data-testid={`video-card-compact-${video.id}`}
      >
        <div className="video-thumbnail-compact">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={video.title} loading="lazy" />
          ) : (
            <div className="placeholder-thumbnail-compact">
              <Video size={24} />
            </div>
          )}
          {durationLabel && (
            <span className="video-duration-badge">{durationLabel}</span>
          )}
        </div>
        <div className="video-info-compact">
          <h4>{video.title}</h4>
          <span className="video-source-compact">{source}</span>
        </div>
      </a>
    );
  }

  return (
    <div className="video-card" data-testid={`video-card-${video.id}`}>
      <div className="video-thumbnail-static">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={video.title} loading="lazy" />
        ) : (
          <div className="placeholder-thumbnail">
            <Video size={48} />
          </div>
        )}
        {durationLabel && (
          <span className="video-duration-overlay">{durationLabel}</span>
        )}
        <div className="external-badge">External Video</div>
      </div>
      
      <div className="video-info">
        <div className="video-header">
          <span className={`video-category-badge ${getCategoryColor()}`}>
            {video.category}
          </span>
          {showMetadata && video.views > 0 && (
            <span className="video-views">
              <Eye size={14} />
              {video.views.toLocaleString()}
            </span>
          )}
        </div>
        
        <h3>{video.title}</h3>
        
        {video.description && (
          <p className="video-description">{cleanDescription(video.description)}</p>
        )}
        
        <div className="video-meta">
          <span className="video-source">
            <Video size={14} />
            {source}
          </span>
          {durationLabel && (
            <span className="video-duration">
              <Clock size={14} />
              {durationLabel}
            </span>
          )}
        </div>

        {/* Tags */}
        {showMetadata && video.tags && video.tags.length > 0 && (
          <div className="video-tags">
            <Tag size={14} />
            {video.tags.slice(0, 4).map(tag => (
              <span key={tag} className="video-tag">{tag}</span>
            ))}
          </div>
        )}
        
        <a 
          href={videoUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="video-external-link"
          onClick={handleVideoClick}
          data-testid={`video-link-${video.id}`}
        >
          <ExternalLink size={16} />
          Watch on YouTube
        </a>
        
        <p className="video-disclaimer">
          This video is provided for general educational purposes only. 
          It was not created by Uro2Go and does not replace medical advice from your physician.
        </p>
      </div>
    </div>
  );
};

export default VideoCard;
